/*
 * CAPTURE-OWNED MODULE
 * The Vendor block on Review (slice 2b-3): the document's vendor snapshot,
 * Display name and Vendor number, each with where it came from (the tenant's
 * vendor record or a reviewer) and, on a document that is not published, set,
 * clear and "Use vendor record".
 *
 * The page owns the snapshot (it comes with GET /documents/:id, a vendor_name
 * correction and a re-extract) and passes it in; every write answers with the
 * new snapshot, handed back through onSnapshot with the document it was for.
 * A response for a document no longer shown is dropped here, and the page
 * checks again before applying it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { captureAPI } from '../services/api';
import './VendorBlock.css';

// The server's limits (capture-app services/vendorRecord/records.ts).
export const DISPLAY_NAME_MAX = 200;
export const VENDOR_NUMBER_MAX = 64;

const ROWS = [
  { field: 'display_name', label: 'Display name', value: 'vendor_display_name', source: 'vendor_display_name_source', max: DISPLAY_NAME_MAX },
  { field: 'vendor_number', label: 'Vendor number', value: 'vendor_number', source: 'vendor_number_source', max: VENDOR_NUMBER_MAX },
];

const SOURCE_LABELS = { vendor_record: 'Vendor record', reviewer: 'Reviewer' };

export const PUBLISHED_TEXT = 'This document is published, so its vendor values cannot change.';
export const NOT_FOUND_TEXT = 'Document not found.';
export const GENERIC_TEXT = 'Could not save. Try again.';

/**
 * Show the block when the tenant keeps vendor records, or when this document
 * already carries something vendor: a record link, a value, or a reviewer's
 * decision (a cleared value is one). Otherwise nothing, so a tenant without
 * vendor records sees Review as before.
 */
export function shouldShowVendorBlock(directoryInUse, snapshot) {
  if (directoryInUse === true) return true;
  if (!snapshot) return false;
  return Boolean(
    snapshot.vendor_id
    || snapshot.vendor_display_name
    || snapshot.vendor_number
    || snapshot.vendor_display_name_source === 'reviewer'
    || snapshot.vendor_number_source === 'reviewer',
  );
}

/** What a failed write says. 409 published also asks the page to refresh. */
export function vendorErrorText(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 409 && data.code === 'published') return PUBLISHED_TEXT;
  if (status === 404) return NOT_FOUND_TEXT;
  if (status === 400) return data.error || GENERIC_TEXT;
  return GENERIC_TEXT;
}

const isPublishedRefusal = (err) => err?.response?.status === 409 && err?.response?.data?.code === 'published';

function SourceChip({ source }) {
  if (!SOURCE_LABELS[source]) return null;
  return (
    <span className={`vb-chip vb-chip-${source}`} data-testid="vb-chip">{SOURCE_LABELS[source]}</span>
  );
}

function shownValue(value, source) {
  if (value) return value;
  if (source === 'reviewer') return 'Cleared by reviewer';
  return '—';
}

function VendorRow({ row, snapshot, readOnly, busy, error, onSave, onRevert }) {
  const value = snapshot?.[row.value] ?? null;
  const source = snapshot?.[row.source] ?? null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Enter and Escape end the edit by blurring; the blur must not save again.
  const skipBlur = useRef(false);

  const begin = () => {
    // An Enter or Escape that ended the last edit unmounted its input, which
    // fires no blur, so the flag can still be set.
    skipBlur.current = false;
    setDraft(value ?? '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    const next = draft.trim() || null;
    if (next === (value ?? null)) return; // unchanged: nothing is sent
    onSave(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      skipBlur.current = true;
      commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      skipBlur.current = true;
      setEditing(false);
    }
  };

  const onBlur = () => {
    if (skipBlur.current) {
      skipBlur.current = false;
      return;
    }
    commit();
  };

  const text = shownValue(value, source);
  const muted = !value;

  return (
    <div className={`vb-row${busy ? ' vb-saving' : ''}`} data-testid={`vb-row-${row.field}`}>
      <span className="vb-label">{row.label}</span>
      <div className="vb-value-cell">
        {readOnly ? (
          <span className={`vb-value${muted ? ' vb-muted' : ''}`} data-testid="vb-value">{text}</span>
        ) : editing ? (
          <input
            className="vb-input"
            data-testid="vb-input"
            aria-label={row.label}
            value={draft}
            maxLength={row.max}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
          />
        ) : (
          <button
            type="button"
            className={`vb-value vb-value-edit${muted ? ' vb-muted' : ''}`}
            data-testid="vb-value"
            title={`Edit ${row.label.toLowerCase()}`}
            onClick={begin}
            disabled={busy}
          >
            {text}
          </button>
        )}
        <SourceChip source={source} />
        {!readOnly && source === 'reviewer' && (
          <button
            type="button"
            className="vb-revert"
            data-testid="vb-revert"
            onClick={onRevert}
            disabled={busy}
          >
            Use vendor record
          </button>
        )}
        {busy && <span className="vb-status" role="status">Saving…</span>}
      </div>
      {error && <div className="vb-error" role="alert">{error}</div>}
    </div>
  );
}

export default function VendorBlock({
  documentId,
  snapshot,
  directoryInUse,
  printedVendorName,
  readOnly = false,
  onSnapshot,
  onPublished,
}) {
  // Which document is shown now, read when a response lands.
  const currentDocument = useRef(documentId);
  currentDocument.current = documentId;
  const [busy, setBusy] = useState({});
  const [errors, setErrors] = useState({});

  // Another document: the last one's saving and error state are not its.
  useEffect(() => {
    setBusy({});
    setErrors({});
  }, [documentId]);

  if (!shouldShowVendorBlock(directoryInUse, snapshot)) return null;

  const run = async (field, call) => {
    const sentFor = documentId;
    setBusy((b) => ({ ...b, [field]: true }));
    setErrors((e) => ({ ...e, [field]: null }));
    try {
      const res = await call();
      if (currentDocument.current !== sentFor) return;
      if (res?.vendor) onSnapshot?.(sentFor, res.vendor);
    } catch (err) {
      if (currentDocument.current !== sentFor) return;
      setErrors((e) => ({ ...e, [field]: vendorErrorText(err) }));
      if (isPublishedRefusal(err)) onPublished?.();
    } finally {
      if (currentDocument.current === sentFor) setBusy((b) => ({ ...b, [field]: false }));
    }
  };

  const printed = typeof printedVendorName === 'string' ? printedVendorName.trim() : '';

  return (
    <section className="vb-block" data-testid="vb-block" aria-label="Vendor">
      <div className="vb-title">Vendor</div>
      {ROWS.map((row) => (
        <VendorRow
          key={`${documentId}:${row.field}`}
          row={row}
          snapshot={snapshot}
          readOnly={readOnly}
          busy={Boolean(busy[row.field])}
          error={errors[row.field] || null}
          onSave={(value) => run(row.field, () => captureAPI.setDocumentVendorValue(documentId, row.field, value))}
          onRevert={() => run(row.field, () => captureAPI.revertDocumentVendorValue(documentId, row.field))}
        />
      ))}
      {!snapshot?.vendor_id && (
        <div className="vb-no-match" data-testid="vb-no-match">
          {printed ? `No vendor record matches '${printed}'` : 'No vendor record matches'}
        </div>
      )}
    </section>
  );
}
